import { useCallback, useEffect, useRef, useState } from "react";
import AuthImage from "@/components/AuthImage";
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";

const MIN = 1, MAX = 5, STEP = 0.5;

// Full-screen photo gallery with prev/next, keyboard nav, zoom and pan.
export default function PhotoLightbox({ photos = [], captions = {}, index, onIndexChange, onClose }) {
  const total = photos.length;
  const open = index !== null && index !== undefined && total > 0;
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef(null);
  const touch = useRef(null);
  const scaleRef = useRef(1);
  const offsetRef = useRef({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);

  useEffect(() => { scaleRef.current = scale; }, [scale]);
  useEffect(() => { offsetRef.current = offset; }, [offset]);

  const reset = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, []);
  const go = useCallback((dir) => { reset(); onIndexChange((index + dir + total) % total); }, [index, total, onIndexChange, reset]);
  const zoomBy = useCallback((delta) => {
    setScale((s) => {
      const next = Math.min(MAX, Math.max(MIN, +(s + delta).toFixed(2)));
      if (next === 1) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  useEffect(() => { reset(); }, [index, reset]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "+" || e.key === "=") zoomBy(STEP);
      else if (e.key === "-") zoomBy(-STEP);
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, go, onClose, zoomBy, reset]);

  if (!open) return null;

  const onWheel = (e) => { e.preventDefault(); zoomBy(e.deltaY < 0 ? STEP : -STEP); };
  const onDown = (e) => { if (scale <= 1) return; drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y }; setDragging(true); };
  const onMove = (e) => { if (!drag.current) return; setOffset({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }); };
  const onUp = () => { drag.current = null; setDragging(false); };
  const zoomed = scale > 1;

  const dist2 = (touches) => Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      touch.current = { mode: "pinch", startDist: dist2(e.touches), startScale: scaleRef.current };
    } else if (e.touches.length === 1) {
      const p = e.touches[0];
      if (scaleRef.current > 1) touch.current = { mode: "pan", x: p.clientX - offsetRef.current.x, y: p.clientY - offsetRef.current.y };
      else touch.current = { mode: "swipe", startX: p.clientX, startY: p.clientY, dx: 0, dy: 0 };
    }
  };
  const onTouchMove = (e) => {
    const t = touch.current;
    if (!t) return;
    if (t.mode === "pinch" && e.touches.length === 2) {
      const next = Math.min(MAX, Math.max(MIN, +(t.startScale * (dist2(e.touches) / t.startDist)).toFixed(2)));
      setScale(next);
      if (next === 1) setOffset({ x: 0, y: 0 });
    } else if (t.mode === "pan" && e.touches.length === 1) {
      const p = e.touches[0];
      setOffset({ x: p.clientX - t.x, y: p.clientY - t.y });
    } else if (t.mode === "swipe" && e.touches.length === 1) {
      t.dx = e.touches[0].clientX - t.startX;
      t.dy = e.touches[0].clientY - t.startY;
    }
  };
  const onTouchEnd = () => {
    const t = touch.current;
    if (t && t.mode === "swipe" && Math.abs(t.dx) > 50 && Math.abs(t.dx) > Math.abs(t.dy)) go(t.dx < 0 ? 1 : -1);
    touch.current = null;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col" data-testid="photo-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-3 text-white/90" onClick={(e) => e.stopPropagation()}>
        <span className="text-sm font-medium" data-testid="lightbox-caption">{captions[photos[index]] || ""}</span>
        <div className="flex items-center gap-2">
          <button onClick={() => zoomBy(-STEP)} disabled={scale <= MIN} className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="lightbox-zoom-out" aria-label="Riduci"><ZoomOut className="h-5 w-5" /></button>
          <span className="text-xs tabular-nums w-10 text-center" data-testid="lightbox-zoom-level">{Math.round(scale * 100)}%</span>
          <button onClick={() => zoomBy(STEP)} disabled={scale >= MAX} className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="lightbox-zoom-in" aria-label="Ingrandisci"><ZoomIn className="h-5 w-5" /></button>
          <button onClick={reset} disabled={!zoomed} className="rounded-full p-2 hover:bg-white/10 disabled:opacity-30 transition-colors" data-testid="lightbox-zoom-reset" aria-label="Reimposta zoom"><Maximize2 className="h-5 w-5" /></button>
          <span className="text-sm font-medium tabular-nums ml-2" data-testid="lightbox-counter">{index + 1} / {total}</span>
          <button onClick={onClose} className="rounded-full p-2 hover:bg-white/10 transition-colors" data-testid="lightbox-close" aria-label="Chiudi"><X className="h-6 w-6" /></button>
        </div>
      </div>

      <div
        className="flex-1 flex items-center justify-center px-4 pb-4 min-h-0 overflow-hidden select-none"
        onClick={(e) => e.stopPropagation()}
        onWheel={onWheel}
        onMouseDown={onDown}
        onMouseMove={onMove}
        onMouseUp={onUp}
        onMouseLeave={onUp}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onDoubleClick={() => (zoomed ? reset() : setScale(2.5))}
        style={{ cursor: zoomed ? (dragging ? "grabbing" : "grab") : "zoom-in", touchAction: "none" }}
      >
        {total > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(-1); }} className="absolute left-3 sm:left-6 z-10 rounded-full bg-white/10 hover:bg-white/20 p-3 text-white transition-colors" data-testid="lightbox-prev" aria-label="Precedente"><ChevronLeft className="h-6 w-6" /></button>
        )}
        <AuthImage
          key={photos[index]}
          src={photos[index]}
          draggable={false}
          className="max-h-full max-w-full object-contain rounded-lg"
          style={{ transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`, transition: dragging ? "none" : "transform 0.15s ease-out" }}
          data-testid="lightbox-image"
        />
        {total > 1 && (
          <button onClick={(e) => { e.stopPropagation(); go(1); }} className="absolute right-3 sm:right-6 z-10 rounded-full bg-white/10 hover:bg-white/20 p-3 text-white transition-colors" data-testid="lightbox-next" aria-label="Successiva"><ChevronRight className="h-6 w-6" /></button>
        )}
      </div>

      {total > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 py-3 justify-center" onClick={(e) => e.stopPropagation()}>
          {photos.map((src, i) => (
            <button key={i} onClick={() => onIndexChange(i)} className={`h-14 w-20 shrink-0 rounded-md overflow-hidden border-2 transition-colors ${i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`} data-testid={`lightbox-thumb-${i}`}>
              <AuthImage src={src} className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
